import { useEffect, useState } from "react";
import { Check, Download } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { trackProductView } from "@/lib/attribution";
import Seo from "@/components/Seo";

const SITE = "https://voluntoldhq.com";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INSIDE = [
  "The four SNAP steps (See it, Name it, Anchor, Play), each in one line you can remember under pressure",
  "The Five Opponents: what each one sounds like mid-game, and a quick fix for each",
  "\u201cUse it in every seat\u201d: when to SNAP and what \u201cPlay\u201d looks like for players, parents and coaches",
  "Printable wallet cards for a bag, a locker or a clipboard",
];
const BODY = [
  "I\u2019m you. I\u2019ve been the player with a tight chest before the snap, the parent replaying a bad game on the drive home, and the coach watching a kid\u2019s head drop after one mistake. Same game, different seat. Every seat has the same problem: one bad moment turns into five.",
  "SNAP is how you stop it. See it. Name it. Anchor. Play. Four steps that fit between plays, on one card you can keep in a bag, on a phone, or taped inside a locker. Enter your email and it\u2019s yours, free.",
];

function SnapForm() {
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) { setErr("Enter a valid email address."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/snap-card", { email: clean, first_name: first.trim() || null });
      trackEvent("generate_lead", { lead_source: "snap_card", value: 0, currency: "USD" });
      try { localStorage.setItem("vt_news_popup", "subscribed"); } catch { /* ignore */ }
      setDone(data);
    } catch (ex) {
      setErr(ex?.response?.status === 429 ? "Too many tries. Give it a few minutes." : "Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="border-2 border-[#1E1E1E] bg-[#1E1E1E] text-[#F5F0EA] p-6 sm:p-8" data-testid="snap-thanks">
      <span className="kicker" style={{ color: "#DB9130" }}>You&apos;re in</span>
      <h2 className="mt-2 font-head uppercase text-2xl sm:text-3xl">Your SNAP Card is ready.</h2>
      <p className="mt-3 text-sm text-[#D8D3C7] leading-relaxed">We also emailed it to you, so it&apos;s there when you need it on game day.</p>
      <a href={done.download_url} data-testid="snap-download-btn"
        className="mt-6 inline-flex items-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-[#D6922C] transition-colors">
        <Download size={16} /> Download the SNAP Card
      </a>
    </div>
  );

  return (
    <form onSubmit={submit} className="border-2 border-[#D3CFC0] bg-white p-6 sm:p-8 space-y-4" data-testid="snap-form" noValidate>
      <label className="block">
        <span className="kicker text-[#6E6E6E]">First name (optional)</span>
        <input value={first} onChange={(e) => setFirst(e.target.value)} maxLength={60} data-testid="snap-first-name"
          className="mt-1.5 w-full border-2 border-[#D3CFC0] px-3 py-2.5 text-[#1E1E1E] focus:border-[#1E1E1E] outline-none" />
      </label>
      <label className="block">
        <span className="kicker text-[#6E6E6E]">Email</span>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="snap-email"
          className="mt-1.5 w-full border-2 border-[#D3CFC0] px-3 py-2.5 text-[#1E1E1E] focus:border-[#1E1E1E] outline-none" />
      </label>
      {err && <p className="text-sm text-[#B23A2E]" data-testid="snap-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="snap-submit"
        className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-7 py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
        {busy ? "Sending\u2026" : "Send me the SNAP Card"}
      </button>
      <p className="text-xs text-[#6E6E6E]">Free. You&apos;ll also get the VoluntoldHQ newsletter; unsubscribe anytime.</p>
    </form>
  );
}

export default function SnapCard() {
  const [img, setImg] = useState(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackProductView(api, "snap_card");
    trackEvent("view_item", { currency: "USD", value: 0, items: [{ item_id: "snap_card", item_name: "The SNAP Card" }] });
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16" data-testid="snap-card-page">
      <Seo title="The SNAP Card" brand="VoluntoldHQ" path="/snap-card" image={`${SITE}/products/snap-card-cover.png`}
        description="The five-second reset for the moment the game gets in your head. Free for players, sports parents and coaches." />
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-start">
        <div>
          <span className="kicker" style={{ color: "#DB9130" }}>Free download</span>
          <h1 className="mt-2 font-head font-semibold uppercase text-4xl sm:text-5xl lg:text-6xl text-[#1E1E1E] leading-[1.02]" data-testid="snap-title">The SNAP Card</h1>
          <p className="mt-4 text-base md:text-lg text-[#3A3A3A]" data-testid="snap-subhead">The five-second reset for the moment the game gets in your head.</p>
          <div className="mt-6 space-y-4 text-[#3A3A3A] leading-relaxed max-w-xl">{BODY.map((t) => <p key={t.slice(0, 12)}>{t}</p>)}</div>
          <h2 className="mt-8 font-head uppercase text-lg text-[#1E1E1E]">What&apos;s inside</h2>
          <ul className="mt-3 space-y-2" data-testid="snap-inside">
            {INSIDE.map((t) => <li key={t} className="flex gap-2 text-sm text-[#1E1E1E]"><Check size={15} className="mt-0.5 text-[#DB9130] shrink-0" />{t}</li>)}
          </ul>
          <p className="mt-6 text-sm text-[#3A3A3A]" data-testid="snap-audience"><strong>Who it&apos;s for:</strong> Players, sports parents and coaches, any sport, any level.</p>
        </div>
        <div className="space-y-6 lg:sticky lg:top-24">
          {img && <img src="/products/snap-card-cover.png" alt="The SNAP Card cover" onError={() => setImg(false)}
            className="w-full max-w-sm mx-auto border-2 border-[#D3CFC0] bg-[#E7E3D9]" data-testid="snap-cover" />}
          <SnapForm />
        </div>
      </div>
    </div>
  );
}
